import { BloodType } from "@qendo/types";
import { useEffect, useId, useState } from "react";
import { Button } from "~/components/Button";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { FieldGroup } from "~/components/form/FieldGroup";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Space } from "~/constants/sizing";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";

type BloodTypeFormProps = {
    onRequestClose: () => void;
} & FullPageModalProps;

export const BloodTypeForm = ({ onRequestClose, ...modalProps }: BloodTypeFormProps) => {
    const { profile, can_update, updateProfile } = useProfile();
    const [option, setOption] = useState<BloodType | null>(profile.blood_type ?? null);
    const { addToast, saving, setSaving } = useFeedback();
    const id = useId();

    const canSave = can_update && option !== null && !saving;

    const dirty = option !== (profile.blood_type ?? null);

    const doSave = () => {
        if (!canSave) {
            return;
        }
        setSaving(id);
    };

    const commit = () => {
        updateProfile({ blood_type: option as BloodType })
            .then(() => {
                addToast({
                    icon: "checkmark-circle",
                    content: "Blood type updated",
                    duration: 3000,
                });

                onRequestClose();
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            title="Blood type"
            cancelable
            dirty={dirty}
            headerRight={() => (
                <Button title="Save" size="sm" disabled={!canSave} onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
            gap={Space.lg}
        >
            <FieldGroup>
                <ComboBox<BloodType>
                    items={Object.values(BloodType)}
                    value={option}
                    onItemSelected={setOption}
                    label="Blood type"
                    disabled={is_saving}
                />
            </FieldGroup>
        </FullPageModal>
    );
};
